import { z } from "zod";

export interface BrowserStorageEntry<T> {
  key: string;
  storage: "local" | "session";
  schema: z.ZodType<T>;
}

function getStorage(storage: "local" | "session"): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  return storage === "local" ? window.localStorage : window.sessionStorage;
}

export const browserStorage = {
  read<T>(entry: BrowserStorageEntry<T>): T | undefined {
    try {
      const stored = getStorage(entry.storage)?.getItem(entry.key);
      if (stored == null) return undefined;

      const value: unknown = JSON.parse(stored);
      const result = entry.schema.safeParse(value);
      return result.success ? result.data : undefined;
    } catch {
      return undefined;
    }
  },

  write<T>(entry: BrowserStorageEntry<T>, value: T): boolean {
    try {
      const result = entry.schema.safeParse(value);
      if (!result.success) return false;

      const serialized = JSON.stringify(result.data);
      if (serialized === undefined) return false;

      const storage = getStorage(entry.storage);
      if (!storage) return false;
      storage.setItem(entry.key, serialized);
      return true;
    } catch {
      return false;
    }
  },

  remove<T>(entry: BrowserStorageEntry<T>): boolean {
    try {
      const storage = getStorage(entry.storage);
      if (!storage) return false;
      storage.removeItem(entry.key);
      return true;
    } catch {
      return false;
    }
  },
};

export const storageEntries = {
  playbackSettings: {
    key: "situate.playback.settings",
    storage: "local",
    schema: z.object({ pauseOnSeek: z.boolean() }),
  },
  dataCollectionNoticeDismissed: {
    key: "situate:data-collection-notice-dismissed",
    storage: "local",
    schema: z.boolean(),
  },
  ssoRedirectPending: {
    key: "situate:sso_redirect_pending",
    storage: "session",
    schema: z.literal(1),
  },
  preferredArea(deploymentId: string): BrowserStorageEntry<string> {
    return {
      key: `situate:v1:deployment:${encodeURIComponent(deploymentId)}:preferred-area`,
      storage: "local",
      schema: z.string().min(1),
    };
  },
} as const;
